/**
 * «Как в бункере» — public, framework-agnostic Web Audio entry point.
 *
 * Usage (consumer wires it into their own graph):
 *
 *   await BunkerAudioFilter.register(ctx);
 *   const filter = new BunkerAudioFilter(ctx);
 *   sourceNode.connect(filter.input);
 *   filter.output.connect(nextNode);
 *   filter.setParams({ gateThresholdDb: -52 });
 *   // …later
 *   filter.destroy();
 *
 * The processor is one AudioWorkletNode, so `input` and `output` are the
 * same node. Kept dependency-free (no React / app imports) so the module
 * can be packaged and licensed independently.
 */
import { DEFAULT_PARAMS, type BunkerFilterParams, type BunkerParamMessage } from './types';
// Real same-origin worklet asset (Vite emits /assets/bunkerProcessor-*.js).
// A Blob URL would be blocked by a strict CSP (`script-src 'self'`); a
// same-origin asset is allowed, just like the RNNoise worklet.
import bunkerWorkletUrl from './bunkerProcessor.js?url';

const PROCESSOR_NAME = 'bunker-filter';

// Contexts that have already loaded the worklet module — registering the
// same processor name twice on one context throws.
const registered = new WeakSet<BaseAudioContext>();

export class BunkerAudioFilter {
  readonly node: AudioWorkletNode;

  constructor(context: BaseAudioContext, params: Partial<BunkerFilterParams> = {}) {
    if (!registered.has(context)) {
      throw new Error('BunkerAudioFilter.register(context) must be awaited before construction');
    }
    this.node = new AudioWorkletNode(context, PROCESSOR_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
    });
    // Always push the canonical defaults so the worklet uses them regardless
    // of its own inlined fallback, then apply any caller overrides.
    this.setParams({ ...DEFAULT_PARAMS, ...params });
  }

  /** The single worklet node serves as both ends of the filter. */
  get input(): AudioNode {
    return this.node;
  }
  get output(): AudioNode {
    return this.node;
  }

  /** Push a partial param update to the realtime processor. */
  setParams(params: Partial<BunkerFilterParams>): void {
    const message: BunkerParamMessage = { type: 'params', params };
    this.node.port.postMessage(message);
  }

  /** Detach from the graph. */
  destroy(): void {
    try {
      this.node.port.onmessage = null;
      this.node.disconnect();
    } catch {
      /* already detached */
    }
  }

  /**
   * Load the worklet module into the given context. Idempotent per context.
   * Must be awaited before constructing a filter on that context.
   */
  static async register(context: BaseAudioContext): Promise<void> {
    if (registered.has(context)) return;
    await context.audioWorklet.addModule(bunkerWorkletUrl);
    registered.add(context);
  }
}

export { DEFAULT_PARAMS };
export type { BunkerFilterParams };
